import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { recordLoginEvent } from '../hooks/useSecurity'
import { Spinner, VStack, Text, Banner } from 'wss3-forge'

// Extract profile data from OAuth metadata
function extractOAuthProfileData(meta: Record<string, any>) {
  const iss = meta?.iss || ''

  let firstName = null
  let lastName = null
  let displayName = null
  let avatarUrl = null

  // Discord
  if (iss.includes('discord')) {
    displayName = meta?.custom_claims?.global_name || meta?.full_name
    avatarUrl = meta?.avatar_url
  }
  // Google
  else if (iss.includes('google')) {
    firstName = meta?.given_name
    lastName = meta?.family_name
    displayName = meta?.name
    avatarUrl = meta?.picture
  }
  // Microsoft/Azure
  else if (iss.includes('microsoft') || iss.includes('login.microsoftonline')) {
    firstName = meta?.given_name
    lastName = meta?.family_name
    displayName = meta?.name
    avatarUrl = meta?.picture
  }
  // GitHub
  else if (iss.includes('github') || meta?.user_name) {
    displayName = meta?.name || meta?.user_name
    avatarUrl = meta?.avatar_url
    if (displayName && displayName.includes(' ')) {
      const parts = displayName.split(' ')
      firstName = parts[0]
      lastName = parts.slice(1).join(' ')
    }
  }
  // Fallback
  else {
    displayName = meta?.name || meta?.full_name
    avatarUrl = meta?.avatar_url || meta?.picture
  }

  return { firstName, lastName, displayName, avatarUrl }
}

export function AuthCallback() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {

      // Check for error in URL params
      const urlError = searchParams.get('error')
      if (urlError) {
        setError(urlError)
        return
      }

      // Handle email change confirmation
      const type = searchParams.get('type')
      if (type === 'email_change' || type === 'email') {
        // Email was changed successfully, user should already be logged in
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          // Email change confirmed, go to home
          navigate('/')
          return
        }
        // If no session, continue to normal flow (will prompt login)
      }

      // Check if this is a Cfx.re callback (Supabase has already created the session)
      const provider = searchParams.get('provider')

      if (provider === 'cfxre') {
        // Cfx.re callback - session should already be created by Supabase magic link
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()

        if (sessionError || !session) {
          console.error('Cfx.re session error:', sessionError)
          setError('Failed to complete Cfx.re authentication')
          return
        }

        // Check if this is a provider linking completion
        const linkComplete = searchParams.get('link_complete') === 'true'
        if (linkComplete) {
          // Cfx.re was just linked to the account, redirect to app
          const appId = searchParams.get('app_id')
          const redirectUri = searchParams.get('redirect_uri')

          // Get PKCE/OAuth params - prefer URL params (passed from cfx-auth-callback), fallback to sessionStorage
          const state = searchParams.get('client_state') || sessionStorage.getItem('webba_auth_state')
          const codeChallenge = searchParams.get('client_code_challenge') || sessionStorage.getItem('webba_auth_code_challenge')
          const codeChallengeMethod = searchParams.get('client_code_challenge_method') || sessionStorage.getItem('webba_auth_code_challenge_method')

          // DEBUG: Log state sources
          console.log('[DEBUG] link_complete flow - state sources:', {
            fromUrl: searchParams.get('client_state'),
            fromSessionStorage: sessionStorage.getItem('webba_auth_state'),
            finalState: state,
            allUrlParams: Object.fromEntries(searchParams.entries())
          })

          if (appId && redirectUri) {
            // Clear stored data
            sessionStorage.removeItem('webba_auth_state')
            sessionStorage.removeItem('webba_auth_code_challenge')
            sessionStorage.removeItem('webba_auth_code_challenge_method')
            sessionStorage.removeItem('webba_auth_app_id')
            sessionStorage.removeItem('webba_auth_redirect_uri')

            try {
              const { data: code, error: codeError } = await supabase.rpc('generate_auth_code', {
                p_user_id: session.user.id,
                p_app_id: appId,
                p_redirect_uri: redirectUri,
                p_code_challenge: codeChallenge || null,
                p_code_challenge_method: codeChallengeMethod || null,
                p_state: state || null
              })

              if (!codeError && code) {
                const url = new URL(redirectUri)
                url.searchParams.set('code', code)
                if (state) url.searchParams.set('state', state)
                window.location.href = url.toString()
                return
              }
            } catch (err) {
              console.error('Failed to generate auth code:', err)
            }
          }

          // If no redirect, go home
          navigate('/')
          return
        }

        // Record login event
        await recordLoginEvent(session.user.id, 'cfxre')

        // Get app redirect from URL params (set by cfx-auth-callback) or sessionStorage
        let appId = searchParams.get('app_id') || sessionStorage.getItem('webba_auth_app_id')
        let redirectUri = searchParams.get('redirect_uri') || sessionStorage.getItem('webba_auth_redirect_uri')

        // Get PKCE/OAuth params from sessionStorage (stored before CFX.re redirect)
        const state = sessionStorage.getItem('webba_auth_state')
        const codeChallenge = sessionStorage.getItem('webba_auth_code_challenge')
        const codeChallengeMethod = sessionStorage.getItem('webba_auth_code_challenge_method')

        if (appId && redirectUri) {
          // Clear stored data
          sessionStorage.removeItem('webba_auth_app_id')
          sessionStorage.removeItem('webba_auth_redirect_uri')
          sessionStorage.removeItem('webba_auth_state')
          sessionStorage.removeItem('webba_auth_code_challenge')
          sessionStorage.removeItem('webba_auth_code_challenge_method')

          try {
            // Generate auth code and redirect to app immediately
            // Even for users with placeholder email - they can add email later from Account page
            const { data: code, error: codeError } = await supabase.rpc('generate_auth_code', {
              p_user_id: session.user.id,
              p_app_id: appId,
              p_redirect_uri: redirectUri,
              p_code_challenge: codeChallenge || null,
              p_code_challenge_method: codeChallengeMethod || null,
              p_state: state || null
            })

            if (codeError) throw codeError

            const url = new URL(redirectUri)
            url.searchParams.set('code', code)
            if (state) url.searchParams.set('state', state)
            window.location.href = url.toString()
            return
          } catch (err) {
            console.error('Failed to generate auth code:', err)
          }
        }

        // Navigate to home
        navigate('/')
        return
      }

      // Check if this is a provider linking callback
      const isLinkProvider = searchParams.get('link_provider') === 'true'

      // Standard OAuth callback handling
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()

      if (sessionError || !session) {
        console.error('Auth callback error:', sessionError)
        navigate('/login?error=auth_failed')
        return
      }

      // Handle provider linking flow
      if (isLinkProvider) {
        // Priority: URL params (most reliable) > sessionStorage (fallback)
        const linkProvider = searchParams.get('link_provider_type') || sessionStorage.getItem('webba_link_provider')
        const linkAppId = searchParams.get('link_app_id') || sessionStorage.getItem('webba_link_app_id')
        const linkRedirectUri = searchParams.get('link_redirect_uri') || sessionStorage.getItem('webba_link_redirect_uri')

        if (linkProvider && linkAppId) {
          // Get OAuth identity info from the session
          const identities = session.user.identities || []
          const oauthIdentity = identities.find(i => i.provider === linkProvider)

          if (oauthIdentity) {
            // Link the OAuth identity to the user
            try {
              await supabase.from('user_identities').upsert({
                user_id: session.user.id,
                provider: linkProvider,
                provider_user_id: oauthIdentity.id,
                provider_username: session.user.user_metadata?.name || session.user.user_metadata?.user_name || null,
                provider_email: session.user.email,
                provider_avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null
              }, {
                onConflict: 'provider,provider_user_id'
              })

              // Try to adopt email from the linked provider if user has placeholder email
              if (session.user.email?.includes('@cfx.webba-creative.com')) {
                try {
                  await supabase.functions.invoke('adopt-provider-email', {
                    body: { provider: linkProvider }
                  })
                  // Refresh session to get updated user data
                  await supabase.auth.refreshSession()
                } catch (adoptErr) {
                  console.error('Failed to adopt provider email:', adoptErr)
                  // Non-blocking - continue with redirect
                }
              }

              // Clear session storage
              sessionStorage.removeItem('webba_link_provider')
              sessionStorage.removeItem('webba_link_app_id')
              sessionStorage.removeItem('webba_link_redirect_uri')

              // Redirect back to app with auth code
              if (linkRedirectUri) {
                const { data: code, error: codeError } = await supabase.rpc('generate_auth_code', {
                  p_user_id: session.user.id,
                  p_app_id: linkAppId,
                  p_redirect_uri: linkRedirectUri,
                  p_code_challenge: null,
                  p_code_challenge_method: null,
                  p_state: null
                })

                if (!codeError && code) {
                  const url = new URL(linkRedirectUri)
                  url.searchParams.set('code', code)
                  window.location.href = url.toString()
                  return
                }
              }
            } catch (err) {
              console.error('Failed to link provider:', err)
            }
          }
        }

        // If linking failed or no redirect, go home
        navigate('/')
        return
      }

      // Create/update user profile with OAuth data (only if profile doesn't exist or fields are empty)
      try {
        const meta = session.user.user_metadata
        const { firstName, lastName, displayName, avatarUrl } = extractOAuthProfileData(meta)

        // Check if profile already exists
        const { data: existingProfile } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, display_name, avatar_url')
          .eq('user_id', session.user.id)
          .single()

        if (!existingProfile) {
          // No profile exists, create one
          await supabase.from('user_profiles').insert({
            user_id: session.user.id,
            first_name: firstName,
            last_name: lastName,
            display_name: displayName,
            avatar_url: avatarUrl
          })
        } else {
          // Profile exists, only update empty fields
          const updates: Record<string, string> = {}
          if (!existingProfile.first_name && firstName) updates.first_name = firstName
          if (!existingProfile.last_name && lastName) updates.last_name = lastName
          if (!existingProfile.display_name && displayName) updates.display_name = displayName
          if (!existingProfile.avatar_url && avatarUrl) updates.avatar_url = avatarUrl

          if (Object.keys(updates).length > 0) {
            await supabase
              .from('user_profiles')
              .update(updates)
              .eq('user_id', session.user.id)
          }
        }
      } catch (err) {
        console.error('Failed to sync profile:', err)
        // Don't block login if profile sync fails
      }

      // Also link the OAuth identity to user_identities for provider linking
      const authProvider = session.user.app_metadata?.provider || 'oauth'
      const identities = session.user.identities || []
      const oauthIdentity = identities.find(i => i.provider === authProvider)

      if (oauthIdentity) {
        try {
          await supabase.from('user_identities').upsert({
            user_id: session.user.id,
            provider: authProvider,
            provider_user_id: oauthIdentity.id,
            provider_username: session.user.user_metadata?.name || session.user.user_metadata?.user_name || null,
            provider_email: session.user.email,
            provider_avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || null
          }, {
            onConflict: 'provider,provider_user_id'
          })
        } catch (err) {
          console.error('Failed to sync identity:', err)
        }
      }

      // Record OAuth login event
      await recordLoginEvent(session.user.id, `oauth_${authProvider}`)

      // Check if we have redirect info from OAuth flow
      // Priority: URL params (most reliable) > sessionStorage (fallback)
      const appId = searchParams.get('app_id') || sessionStorage.getItem('webba_auth_app_id')
      const redirectUri = searchParams.get('redirect_uri') || sessionStorage.getItem('webba_auth_redirect_uri')

      // Get PKCE/OAuth params from sessionStorage
      const state = sessionStorage.getItem('webba_auth_state')
      const codeChallenge = sessionStorage.getItem('webba_auth_code_challenge')
      const codeChallengeMethod = sessionStorage.getItem('webba_auth_code_challenge_method')

      if (appId && redirectUri) {
        // Check if app requires a specific provider to be linked
        const { data: appData } = await supabase
          .from('applications')
          .select('required_provider')
          .eq('id', appId)
          .single()

        if (appData?.required_provider) {
          // Check if user has this provider linked
          const { data: identity } = await supabase
            .from('user_identities')
            .select('id')
            .eq('user_id', session.user.id)
            .eq('provider', appData.required_provider)
            .maybeSingle()

          if (!identity) {
            // User doesn't have the required provider linked, redirect to link page
            // Pass all OAuth params in URL to preserve them through the linking flow
            const linkParams = new URLSearchParams({
              app_id: appId,
              redirect_uri: redirectUri
            })
            if (state) linkParams.set('state', state)
            if (codeChallenge) linkParams.set('code_challenge', codeChallenge)
            if (codeChallengeMethod) linkParams.set('code_challenge_method', codeChallengeMethod)
            navigate(`/link-provider?${linkParams.toString()}`)
            return
          }
        }

        // Clear stored data
        sessionStorage.removeItem('webba_auth_app_id')
        sessionStorage.removeItem('webba_auth_redirect_uri')
        sessionStorage.removeItem('webba_auth_state')
        sessionStorage.removeItem('webba_auth_code_challenge')
        sessionStorage.removeItem('webba_auth_code_challenge_method')

        try {
          // Generate auth code
          const { data: code, error: codeError } = await supabase.rpc('generate_auth_code', {
            p_user_id: session.user.id,
            p_app_id: appId,
            p_redirect_uri: redirectUri,
            p_code_challenge: codeChallenge || null,
            p_code_challenge_method: codeChallengeMethod || null,
            p_state: state || null
          })

          if (codeError) throw codeError

          // Redirect to app with code
          const url = new URL(redirectUri)
          url.searchParams.set('code', code)
          if (state) url.searchParams.set('state', state)
          window.location.href = url.toString()
          return
        } catch (err) {
          console.error('Failed to generate auth code:', err)
          navigate('/login?error=code_generation_failed')
          return
        }
      }

      // No redirect, go to home
      navigate('/')
    }

    handleCallback()
  }, [navigate, searchParams])

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-primary)',
        padding: '1rem'
      }}>
        <VStack gap="md" style={{ alignItems: 'center', maxWidth: '400px' }}>
          <Banner variant="error" title="Authentication Failed">
            {error}
          </Banner>
          <Text color="muted">
            <a href="/login" style={{ color: 'var(--brand-primary)' }}>
              Return to login
            </a>
          </Text>
        </VStack>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-primary)'
    }}>
      <VStack gap="md" style={{ alignItems: 'center' }}>
        <Spinner size="lg" />
        <Text color="muted">Signing you in...</Text>
      </VStack>
    </div>
  )
}
